import React, { useState } from "react";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Grid,
  Typography,
  Checkbox,
  Box
} from "@mui/material";
import forcePowersData from "./data/forcePowers.json";
import forceAbilitiesData from "./data/forceAbilities.json";
import MarkedText from "./Util/MarkedText";
import { getEffective } from "./Util/applyHomebrew";
import { CAREER_POWERS, getSpecPowers } from "./data/careerPowers";

function TableComponent({
  tableData,
  forceAbilities,
  character,
  handleCharacterChange,
  selectedForcePower,
  validPowers,
  careerPowerGrants,
  careerDiscountPowers
}) {
  const tableRows = [];
  const getLocation = (row, column) => {
    let spans = tableData.AbilityRows[row].AbilitySpan;
    return spans
      .slice(0, column)
      .reduce((sum, num) => parseInt(sum) + parseInt(num), 0);
  };

  const getSize = (span) => {
    return (span * (12 * span - 2)) / span ** 2;
  };

  for (let row in tableData.AbilityRows) {
    tableRows.push(tableData.AbilityRows[row].Abilities);
  }
  const [boughtPowers, setBoughtPowers] = useState(character.ForcePowers);

  let checkedCells = tableData.AbilityRows.map((row) =>
    new Array(row.Abilities.length).fill(false),
  );

  if (boughtPowers[validPowers()[selectedForcePower].Key]) {
    checkedCells = boughtPowers[validPowers()[selectedForcePower].Key];
  }

  const powerKey = validPowers()[selectedForcePower].Key;
  const grants = careerPowerGrants || {};
  const isCareerPower = powerKey === grants.career
    || powerKey === grants.bonus
    || (grants.specGrants || []).some(g => g.bonus1 === powerKey || g.bonus2 === powerKey);
  const hasMentorDiscount = careerDiscountPowers?.has(powerKey) ?? false;

  const isGrantedCell = (row, col) => {
    if (!isCareerPower) return false;
    const paidCosts = boughtPowers[powerKey]?.PaidCosts || {};
    return paidCosts[`${row}-${col}`] === 0;
  };

  function getAlignedColumn(row, location) {
    const abilitySpan = tableData.AbilityRows[row].AbilitySpan;
    for (let i = 0; i < abilitySpan.length; i++) {
      if (
        getLocation(row, i) <= location &&
        getLocation(row, i) + parseInt(abilitySpan[i]) > location
      ) {
        return i;
      }
    }
  }

  const isConnected = (row, column) => {
    const location = getLocation(row, column);
    let spanCount = parseInt(
      tableData.AbilityRows[row].AbilitySpan[column],
    );
    while (spanCount > 0) {
      let spanIndex = spanCount - 1;
      let { Up, Down, Left, Right } =
        tableData.AbilityRows[row].Directions[location + spanIndex];
      const row0IsGranted = isCareerPower && row === 1 && Up &&
        isGrantedCell(0, getAlignedColumn(0, location + spanIndex));
      if (
        row === 0 ||
        row0IsGranted ||
        (Left && checkedCells[row][column - 1]) ||
        (Right && checkedCells[row][column + 1]) ||
        (Up &&
          checkedCells[row - 1][
          getAlignedColumn(row - 1, location + spanIndex)
          ]) ||
        (Down &&
          checkedCells[row + 1][
          getAlignedColumn(row + 1, location + spanIndex)
          ])
      ) {
        return true;
      }
      spanCount--;
    }
    return false;
  };

  const toggleAbility = (row, column) => {
    const cellId = `${row}-${column}`;

    if (isGrantedCell(row, column)) return;

    const isChecking = !checkedCells[row][column];

    const newGrid = { ...checkedCells };
    newGrid[row][column] = isChecking;

    const currentPowerData = boughtPowers[powerKey] || {};
    const paidCosts = { ...(currentPowerData.PaidCosts || {}) };

    let costToApply = 0;

    if (isChecking) {
      const baseCost = parseInt(tableData.AbilityRows[row].Costs[column]);
      const careerDiscount = (row === 0 && hasMentorDiscount) ? 5 : 0;
      const mentorBonus = (row === 0 && character.MentorBonuses[powerKey]) ? 5 : 0;
      const traditionDiscount = character.Mentor.ForceTradition?.Benefit?.Discounts?.find(
        (d) => d.Power === validPowers()[selectedForcePower].AbilityRows[row].Abilities[column]
      )?.Discount || 0;

      costToApply = Math.max(baseCost - careerDiscount - mentorBonus - traditionDiscount, 5);

      paidCosts[cellId] = costToApply;
    } else {
      if (paidCosts[cellId] !== undefined) {
        costToApply = paidCosts[cellId];
      } else {
        const baseCost = parseInt(tableData.AbilityRows[row].Costs[column]);
        const careerDiscount = (row === 0 && hasMentorDiscount) ? 5 : 0;
        const mentorDiscount = (row === 0 && character.MentorBonuses[powerKey]) ? 5 : 0;
        costToApply = Math.max(baseCost - careerDiscount - mentorDiscount, 5);
      }
      delete paidCosts[cellId];
    }

    const updatedPowerEntry = {
      ...newGrid,
      PaidCosts: paidCosts
    };

    const updatedForcePowers = {
      ...boughtPowers,
      [powerKey]: updatedPowerEntry,
    };

    setBoughtPowers(updatedForcePowers);

    handleCharacterChange({
      XP: isChecking
        ? parseInt(character.XP) - costToApply
        : parseInt(character.XP) + costToApply,
      ForcePowers: updatedForcePowers,
    });
  };

  return (
    <TableContainer>
      <Table style={{ tableLayout: "fixed" }}>
        <TableBody>
          {tableRows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, columnIndex) => (
                <TableCell
                  key={rowIndex + columnIndex}
                  colSpan={
                    tableData.AbilityRows[rowIndex].AbilitySpan[columnIndex]
                  }
                  sx={{
                    verticalAlign: "top",
                    padding: "0px",
                    borderBottom: "0px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Grid container>
                    <Grid
                      item
                      xs={
                        1 /
                        tableData.AbilityRows[rowIndex].AbilitySpan[columnIndex]
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "50px",
                      }}
                    >
                      <Paper
                        square
                        sx={{
                          border: "6px solid gray",
                          height: "0px",
                          minWidth: "90px",
                        }}
                        style={{
                          visibility: tableData.AbilityRows[rowIndex]
                            .Directions[
                            getLocation(rowIndex, columnIndex)
                          ].Left
                            ? "visible"
                            : "hidden",
                        }}
                      ></Paper>
                    </Grid>
                    <Grid
                      item
                      xs={getSize(
                        tableData.AbilityRows[rowIndex].AbilitySpan[columnIndex],
                      )}
                    >
                      <Paper sx={{ overflow: "visible", paddingBottom: "10px", position: 'relative' }}>
                        <Grid
                          container
                          sx={{
                            background: "#005700",
                            borderTopLeftRadius: (theme) => theme.shape.borderRadius,
                            borderTopRightRadius: (theme) => theme.shape.borderRadius,
                          }}
                        >
                          <Grid item xs={12} sm={3} md={2}>
                            <Checkbox
                              sx={{
                                padding: "3px",
                                "&.MuiCheckbox-colorPrimary": {
                                  color: "#FFFFFF",
                                },
                                "&.Mui-checked": {
                                  color: isGrantedCell(rowIndex, columnIndex) ? "#FFD700" : "#FFFFFF",
                                },
                                "&.Mui-disabled": {
                                  color: isGrantedCell(rowIndex, columnIndex) ? "#FFD700" : "#808080",
                                },
                              }}
                              checked={checkedCells[rowIndex][columnIndex]}
                              disabled={!isConnected(rowIndex, columnIndex) || isGrantedCell(rowIndex, columnIndex)}
                              onChange={() =>
                                toggleAbility(rowIndex, columnIndex)
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={9} md={10}>
                            {(() => {
                              const ab = forceAbilities.find((ability) => ability.Key === cell);
                              const hasConflict = /conflict/i.test(ab.Description);
                              const hasRepeatable = /repeatable/i.test(ab.Description);
                              return (
                                <Typography variant="h6" color="white" sx={{
                                  position: 'relative',
                                  paddingRight: (hasConflict || hasRepeatable) ? '50px' : undefined,
                                  fontSize: {
                                    xs: '.7rem',
                                    sm: '1rem',
                                    md: '1.25rem',
                                  }
                                }}>
                                  <span style={{ wordWrap: 'break-word' }}>{ab.Name}</span>
                                  {(hasConflict || hasRepeatable) && (
                                    <span style={{ position: 'absolute', right: 3, top: 0, display: 'flex', gap: '3px', alignItems: 'center' }}>
                                      {hasConflict && (
                                        <span dangerouslySetInnerHTML={{ __html: MarkedText.renderer('[CONFLICT]') }} />
                                      )}
                                      {hasRepeatable && (
                                        <svg viewBox="0 0 758 758" style={{ width: '1.2em', height: '1.2em', flexShrink: 0 }} aria-hidden="true">
                                          <path d="M554.592,472.995l94.443,-94.443l-270.482,-270.482l-121.976,121.976l0,-54.164l-53.906,0l175.882,-175.882l378.552,378.552l-148.492,148.492l-54.02,0l0,-54.05Zm-352.233,-188.732l-94.289,94.289l270.482,270.482l121.823,-121.823l0,54.05l54.02,-0l-175.843,175.843l-378.552,-378.552l148.453,-148.453l53.906,0l-0,54.164Z" fill="#fff" />
                                        </svg>
                                      )}
                                    </span>
                                  )}
                                </Typography>
                              );
                            })()}
                          </Grid>
                        </Grid>
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '5px',
                            fontSize: {
                              xs: '0.6rem',
                              sm: '1rem'
                            },
                          }}
                          dangerouslySetInnerHTML={{
                            __html: MarkedText.renderer(
                              forceAbilities.find(
                                (ability) => ability.Key === cell,
                              ).Description,
                            ),
                          }}
                        />
                        <Paper
                          sx={{
                            background: "#005700",
                            display: "inline-block",
                            paddingLeft: "5px",
                            paddingRight: "5px",
                            marginRight: "10px",
                            position: "absolute",
                            bottom: "-10px",
                            right: 0,
                            zIndex: 3
                          }}
                        >
                          <Typography sx={{ fontSize: { xs: '0.7rem', sm: '1rem' }, color: "white" }}>
                            Cost:{" "}
                            {isGrantedCell(rowIndex, columnIndex) ? 0 :
                              Math.max(tableData.AbilityRows[rowIndex].Costs[columnIndex] -
                                ((rowIndex === 0 && hasMentorDiscount) ? 5 : 0) -
                                ((rowIndex === 0 && character.MentorBonuses[validPowers()[selectedForcePower].Key]) ? 5 : 0) -
                                (character.Mentor.ForceTradition?.Benefit?.Discounts?.find(d => d.Power === validPowers()[selectedForcePower].AbilityRows[rowIndex].Abilities[columnIndex])?.Discount || 0),
                                5)
                            }
                          </Typography>
                        </Paper>
                      </Paper>
                    </Grid>
                    <Grid
                      item
                      xs={
                        1 /
                        tableData.AbilityRows[rowIndex].AbilitySpan[columnIndex]
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "50px",
                      }}
                    >
                      <Paper
                        square
                        sx={{
                          border: "6px solid gray",
                          height: "0px",
                          minWidth: "90px",
                        }}
                        style={{
                          visibility: tableData.AbilityRows[rowIndex]
                            .Directions[
                            getLocation(rowIndex, columnIndex)
                          ].Right
                            ? "visible"
                            : "hidden",
                        }}
                      ></Paper>
                    </Grid>
                    <Grid container>
                      {Array.from(
                        {
                          length:
                            tableData.AbilityRows[rowIndex]
                              .AbilitySpan[columnIndex],
                        },
                        (_, index) => (
                          <Grid
                            item
                            xs={
                              12 /
                              tableData.AbilityRows[rowIndex]
                                .AbilitySpan[columnIndex]
                            }
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexDirection: "column",
                              minHeight: "30px",
                            }}
                          >
                            <Paper
                              square
                              sx={{
                                border: "6px solid gray",
                                width: "0px",
                                height: "100%",
                                position: "absolute",
                              }}
                              style={{
                                visibility: tableData.AbilityRows[
                                  rowIndex
                                ].Directions[
                                  getLocation(rowIndex, columnIndex) + index
                                ].Down
                                  ? "visible"
                                  : "hidden",
                              }}
                            ></Paper>
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </Grid>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer >
  );
}

function App({ character, handleCharacterChange, appFiles }) {
  const [selectedForcePower, setSelectedForcePower] = useState("");
  const isReSpecialized = JSON.parse(localStorage.getItem('homebrewState') || '{}').reSpecialized;

  const forcePowers = getEffective('forcePowers', forcePowersData);
  const forceAbilities = getEffective('forceAbilities', forceAbilitiesData);

  const handleForcePowerChange = (event) => {
    setSelectedForcePower(event.target.value);
  };

  const validPowers = () => {
    return forcePowers
      .filter((p) => !p.MinForceRating || p.MinForceRating <= character.ForceRating)
      .sort((a, b) => a.Name.localeCompare(b.Name))
  }

  const careerKey = character.Career?.Key;
  const careerPowerOptions = isReSpecialized ? (CAREER_POWERS[careerKey] || []) : [];
  const careerDiscountPowers = isReSpecialized ? (() => {
    const powers = new Set(careerPowerOptions);
    (character.Specializations || []).forEach(spec => {
      const sp = getSpecPowers(spec);
      if (Array.isArray(sp)) sp.forEach(p => powers.add(p));
    });
    (character.CareerPowerGrants?.specGrants || []).forEach(grant => {
      if (grant.bonus1) powers.add(grant.bonus1);
      if (grant.bonus2) powers.add(grant.bonus2);
    });
    return powers;
  })() : null;

  const mentorToggle = () => {
    let newBonuses = { ...character.MentorBonuses }
    let key = validPowers()[selectedForcePower].Key
    let characterBonus = newBonuses[key]
    if (!characterBonus) {
      newBonuses[key] = true
    }
    else { delete newBonuses[key] }
    handleCharacterChange({ MentorBonuses: newBonuses })
  }

  return (
    <Container>
      <Typography variant="h3">Force Powers</Typography>
      <FormControl fullWidth>
        <InputLabel>Select Force Power</InputLabel>
        <Select value={selectedForcePower} onChange={handleForcePowerChange}>
          {validPowers().map((power, index) => (
            <MenuItem
              key={index}
              value={index}
              sx={(theme) => ({
                backgroundColor: character.ForcePowers[power.Key] ? 'rgba(0, 255, 0, 0.1)' : 'inherit',
                '&:hover': {
                  backgroundColor: character.ForcePowers[power.Key]
                    ? 'rgba(0, 255, 0, 0.2)'
                    : theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 255, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 255, 0, 0.2)',
                  },
                },
              })}
            >
              {power.Name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {validPowers()[selectedForcePower] && (<>
        {!isReSpecialized && (
          <Grid container alignItems="center" justifyContent="flex-end" sx={{ width: '100%' }}>
            <Grid item><Checkbox
              size="small"
              checked={Boolean(character.MentorBonuses?.[validPowers()[selectedForcePower]?.Key] || false)}
              onChange={() => mentorToggle()}
            /></Grid>
            <Grid item><Typography style={{ display: 'flex', alignItems: 'center', height: '100%' }}>Mentor Bonus</Typography></Grid>
          </Grid>
        )}
        <TableComponent
          tableData={validPowers()[selectedForcePower]}
          forceAbilities={forceAbilities}
          character={character}
          handleCharacterChange={handleCharacterChange}
          selectedForcePower={selectedForcePower}
          validPowers={validPowers}
          careerPowerGrants={character.CareerPowerGrants}
          careerDiscountPowers={careerDiscountPowers}
        />
      </>)}
    </Container>
  );
}

export default App;
